import type { SortConfig } from '@/components/widget/widgetTypes';
import type { ContractActionSimple } from '@/views/contracts/contractactions/contractActionTypes';
import type { ContractSimple } from '@/views/contracts/contractsTypes';
import type { MonthlyIndexSimple } from '@/views/monthlyindex/monthlyIndexesTypes';
import type { ServiceContractSimple } from '@/views/servicecontracts/serviceContractsTypes';
import type { TicketSimple } from '@/views/tickets/ticketsTypes';
import type { SortMeta } from '@/stores/tableSort';
import { filterByFieldValue, sortByMeta } from '@/utils/array';

export interface PrefilterTab {
  title: string;
  options: PrefilterOption[];
  initialOptionIndex?: number;
  isClearFilterTab?: boolean;
}
export interface PrefilterOption {
  title: string;
  label?: string;
  filterString?: string;
  sortMeta?: SortMeta[];
  customFilterFn?: (data: any[], focusedColumns: string[]) => any[];
  initialActive?: boolean;

  // Column-related flags (optional, only for some options)
  affectsColumns?: boolean; // does this option change visible columns?
  columnGroupId?: string; // e.g. 'negotiation'
  hiddenColumns?: string[]; // Columns to hide when this option is active
}
export interface PrefilterOptions {
  data: any[];
  filterString?: string;
  sortMeta?: SortMeta[];
  customFilterFn?: (data: any[], focusedColumns: string[]) => any[];
  focusedColumns?: string[];
}
export interface PrefilterApplyOptions {
  data: any[];
  filterString?: string;
  sortMeta?: SortMeta[];
  customFilterFn?: (data: any[], focusedColumns: string[]) => any[];
  focusedColumns: string[];
}

/**
 * Applies filtering and sorting logic in a defined order.
 * 1. Sort (if sortMeta is defined)
 * 2. Filter (if filterString is defined)
 * 3. Custom filter function (last, with full control)
 */
export function applyPrefilter({
  data,
  filterString,
  sortMeta,
  customFilterFn,
  focusedColumns = [],
}: PrefilterApplyOptions): any[] {
  let result = [...data];

  // 1. Sort by sortMeta (if defined)
  if (sortMeta && sortMeta.length) {
    const mappedSortMeta: SortConfig[] = sortMeta.map((s) => ({
      field: s.field,
      order: s.order === 1 ? 'asc' : 'desc',
    }));
    result = [...result].sort(sortByMeta(mappedSortMeta));
  }

  // 2. Filter by filterString (if defined)
  if (filterString) {
    const parts = filterString.split(':');
    if (parts?.length === 2) {
      const field = parts[0]?.trim();
      const rawValue = parts[1]?.trim();
      if (!field || !rawValue) return result;
      const parsed = parseValue(rawValue);
      result = result.filter(filterByFieldValue({ field, hasValue: parsed }));
    }
  }

  // 3. Apply custom filter function
  if (typeof customFilterFn === 'function') {
    result = customFilterFn(result, focusedColumns);
  }

  return result;
}

// Converts strings like 'true', 'false', '123' to typed values
function parseValue(val: string): any {
  if (val === 'true') return true;
  if (val === 'false') return false;
  if (!isNaN(Number(val))) return Number(val);
  return val;
}

/**
 * Creates a prefilter tab only if at least one option returns non-empty results.
 * Validates all options against the data before returning the tab.
 *
 * @param tab - The prefilter tab configuration
 * @param data - The data to test against
 * @returns The tab if valid, undefined otherwise
 */
export function createConditionalPrefilterTab(
  tab: PrefilterTab,
  data: any[],
): PrefilterTab | undefined {
  // Test each option's customFilterFn
  const validOptions = tab.options.filter((option) => {
    if (!option.customFilterFn) return true; // Keep options without filter
    const filtered = option.customFilterFn(data, []);
    return filtered.length > 0;
  });

  // Return undefined if no valid options remain
  if (validOptions.length === 0) return undefined;

  return {
    ...tab,
    options: validOptions,
  };
}

/**
 * Ensures that prefilter tabs have a valid initialActive option.
 * Falls back to the first option if the previously selected option is unavailable.
 *
 * @param tabs - Array of prefilter tabs
 * @returns Tabs with ensured initialActive fallback
 */
export function ensurePrefilterFallback(tabs: PrefilterTab[]): PrefilterTab[] {
  return tabs.map((tab) => {
    // Check if any option has initialActive
    const hasInitialActive = tab.options.some((opt) => opt.initialActive);

    if (!hasInitialActive && tab.options.length > 0) {
      // Set first option as initialActive
      return {
        ...tab,
        options: tab.options.map((opt, idx) => ({
          ...opt,
          initialActive: idx === 0,
        })),
      };
    }

    return tab;
  });
}

export function createCustomerGroupPrefilterTab(customers: any[]): PrefilterTab {
  const groupNames = customers
    .map((c) => {
      // Try to get from meta().rawvalue or fallback to plain value
      return (
        c.customerGroup?.meta?.('rawvalue')?.name ??
        c.customerGroup?.value?.name ??
        c.customerGroup?.name
      );
    })
    .filter((name): name is string => !!name);

  const uniqueGroups = Array.from(new Set(groupNames)).sort();

  const options = uniqueGroups.map((name) => ({
    title: name,
    customFilterFn: (data: any[]) =>
      data.filter((c) => {
        const groupName =
          c.customerGroup?.meta?.('rawvalue')?.name ??
          c.customerGroup?.value?.name ??
          c.customerGroup?.name;
        return groupName === name;
      }),
    sortMeta: [{ field: 'customerGroup.name', order: 1 }],
  }));

  return {
    title: 'pages.customers.tabs.group',
    options,
  };
}

/**
 * Creates a prefilter tab config that groups contracts by contractTypeId.
 */
export function createContractTypePrefilterTab(
  contracts: (ContractSimple | ServiceContractSimple)[],
): PrefilterTab {
  const contractTypes = contracts
    .map((c) => c.contractType)
    .filter((contractType) => !!contractType);

  const uniqueContractTypes = Array.from(new Set(contractTypes));

  const options = uniqueContractTypes.map((contractType) => ({
    title: `enums.${contractType.replace(/\s+/g, '_').toUpperCase()}`,
    customFilterFn: (data: any[]) =>
      data.filter((c) => c.contractType.meta?.('rawvalue') === contractType),
  }));

  return {
    title: 'pages.contracts.tabs.contractType',
    options,
  };
}

/**
 * Creates a prefilter tab that shows tickets by assigned team.
 */
export function createTicketsByTeamsTab(tickets: TicketSimple[]): PrefilterTab {
  const teamKeys = tickets.map((c) => c.team);

  const uniqueTeamkeys = Array.from(new Set(teamKeys));

  const options = uniqueTeamkeys?.map((teamKey) => ({
    title: `enums.${(teamKey ?? 'team_none').replace(' ', '_').toUpperCase()}`,
    customFilterFn: (data: any[]) => data.filter((c) => c.team.meta?.('rawvalue') === teamKey),
  }));

  return {
    title: 'pages.tickets.tabs.teams',
    options,
  };

  // ==> keep for later use
  // return {
  //   title: 'pages.tickets.tabs.status:available',
  //   options: [
  //     {
  //       title: 'enums.AVAILABLE',
  //       customFilterFn: (tickets: any[]) =>
  //         tickets.filter(
  //           (tickets) =>
  //             Array.isArray(tickets.contractAction.meta('rawvalue')) &&
  //             tickets.contractAction.meta('rawvalue').some((action: any) => {
  //               return action.status?.toLowerCase() === 'available';
  //             }),
  //         ),
  //     },
  //   ],
  // };
}

export function createStatusPrefilterTab(data: ContractActionSimple[]): PrefilterTab[] {
  const availableStatuses = [...new Set(data.map((d) => d.status))];

  return [
    {
      title: 'status',
      isClearFilterTab: true,
      options: [
        { title: 'All', label: 'Alle', filterString: '', initialActive: true },
        ...availableStatuses.map((status) => ({
          title: `enums.${status}`,
          filterString: `status:${status}`,
        })),
      ],
    },
  ];
}

export function createContractActionStatusPrefilterTab(
  actions: ContractActionSimple[],
): PrefilterTab {
  const allStatuses = actions.map((a) => a.status).filter(Boolean);
  const uniqueStatuses = Array.from(new Set(allStatuses));

  const options = uniqueStatuses.map((status) => ({
    title: `enums.${status?.toUpperCase()}`,
    customFilterFn: (data: any[]) =>
      data.filter((item) => {
        return item.status?.meta('rawvalue').toLowerCase() === status?.toLowerCase();
      }),
  }));

  return {
    title: 'pages.contracts.tabs.status',
    options,
  };
}

export function createMonthlyIndexPrefilterTab(monthlyIndexes: MonthlyIndexSimple[]) {
  const allYears = monthlyIndexes.map((a) => new Date(a.year).getFullYear());
  const uniqueYears = Array.from(new Set(allYears));

  const options = uniqueYears.map((year) => ({
    title: `${year}`,
    customFilterFn: (data: any[]) =>
      data.filter((item) => {
        return item.year?.value.getFullYear() === year;
      }),
  }));

  return {
    title: 'pages.monthlyindex.tabs.year',
    options,
  };
}
